import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, Calendar, Linkedin } from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import SEO from '@/components/shared/SEO';
import { LocalBusinessSchema } from '@/components/shared/JsonLdSchemas';
import ConsultationSchedulerModal from '@/components/modals/ConsultationSchedulerModal';
import FastQuoteModal from '@/components/modals/FastQuoteModal';
import { useFormAutosave } from '@/hooks/useFormAutosave';
import { reportFailedSubmission } from '@/hooks/useFormFailureBeacon';
import { RestoreDraftBanner } from '@/components/shared/RestoreDraftBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { contactInfo, teamMembers } from '@/data/navigation';
import SeoContentBlock from '@/components/shared/SeoContentBlock';
import { CONTACT_SEO } from '@/content/seoContentCopy';
import { reportAiConversion } from '@/utils/aiAttribution';
import {
  CustomerTypeTabs,
  ProjectContextFields,
  ExistingCustomerFields,
} from '@/pages/contact/CustomerTypeSection';

// States/Provinces for dropdown
const statesProvinces = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

// Roles for dropdown
const roles = [
  "Architect",
  "Business Management/Administration",
  "Designer/Specifier",
  "Engineer",
  "Estimator",
  "Facilities Maintenance",
  "Facilities Management",
  "Other",
  "Owner/Partner/Executive",
  "Procurement",
  "Project Management",
  "Sales/Account Management",
  "Sustainability Management"
];

// Help options for dropdown
const helpOptions = [
  "Contact my rep",
  "Product information",
  "Price information",
  "Sample/Brochure request",
  "Placing an order",
  "Design support",
  "Technical information",
  "Sustainability",
  "Other"
];

// Team Member Card Component
const TeamCard = ({ member }) => (
  <div className="bg-card rounded-lg border border-border p-4 text-center">
    <img
      src={member.image}
      alt={member.name}
      className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
      style={member.imagePosition ? { objectPosition: member.imagePosition } : undefined}
    />
    <h4 className="font-semibold">{member.name}</h4>
    <p className="text-sm text-muted-foreground mb-2">{member.title}</p>
    <div className="text-xs text-muted-foreground space-y-1">
      <a href={`mailto:${member.email}`} className="block hover:text-primary">
        {member.email}
      </a>
      <a href={`tel:${member.phone}`} className="block hover:text-primary">
        {member.phone}
      </a>
    </div>
    {member.linkedin && member.linkedin !== "#" && (
      <div className="mt-3">
        <a
          href={member.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-8 h-8 bg-primary/10 rounded-full items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
        >
          <Linkedin className="w-4 h-4" />
        </a>
      </div>
    )}
  </div>
);

const ContactUsPage = () => {
  const [customerType, setCustomerType] = useState('new'); // 'new' | 'existing'
  const [formData, setFormData] = useState({
    message: '',
    fullName: '',
    email: '',
    phone: '',
    company: '',
    streetAddress: '',
    city: '',
    zip: '',
    projectTitle: '',
    specifiersLocation: '',
    customerAccountNumber: '',
    accountRepresentative: '',
    area: 'United States of America',
    state: '',
    role: '',
    helpType: '',
    subscribeEmail: false,
    agreePrivacy: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const [isFastQuoteOpen, setIsFastQuoteOpen] = useState(false);

  const {
    hasDraft, draftAge, restoreDraft, clearDraft, dismissDraftBanner,
  } = useFormAutosave('contact-us', formData, setFormData, { enabled: !isSuccess });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.company.trim()) newErrors.company = 'Company / Organization is required';
    if (!formData.streetAddress.trim()) newErrors.streetAddress = 'Street address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.projectTitle.trim()) newErrors.projectTitle = 'Project Title / Architect is required';
    if (!formData.specifiersLocation.trim()) newErrors.specifiersLocation = "Specifier's Location is required";
    if (customerType === 'existing') {
      if (!formData.customerAccountNumber.trim()) newErrors.customerAccountNumber = 'Customer Account Number is required';
      if (!formData.accountRepresentative.trim()) newErrors.accountRepresentative = 'Account Representative is required';
    }
    if (!formData.area) newErrors.area = 'Area (Country) is required';
    if (!formData.state) newErrors.state = 'State/Province is required';
    if (!formData.role) newErrors.role = 'Your role is required';
    if (!formData.helpType) newErrors.helpType = 'Please tell us how we can help';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (!formData.agreePrivacy) newErrors.agreePrivacy = 'You must agree to the privacy policy';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Build full address string for the email body so sales sees the complete location
      const fullAddress = [
        formData.streetAddress.trim(),
        formData.city.trim(),
        formData.state,
        formData.zip.trim(),
        formData.area,
      ].filter(Boolean).join(', ');

      const messageWithAddress = `${formData.message}\n\n--- Address ---\n${fullAddress}`;

      const res = await fetch(`/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          subject: formData.helpType || 'General Inquiry',
          message: messageWithAddress,
          customerType,
          projectTitle: formData.projectTitle,
          specifiersLocation: formData.specifiersLocation,
          customerAccountNumber: customerType === 'existing' ? formData.customerAccountNumber : '',
          accountRepresentative: customerType === 'existing' ? formData.accountRepresentative : '',
        }),
      });
      if (!res.ok) throw new Error('Submit failed');
      clearDraft();
      reportAiConversion({ form_type: 'contact', contact_email: formData.email });
      setIsSuccess(true);
    } catch (err) {
      console.error('Contact form error:', err);
      reportFailedSubmission({
        formType: 'contact',
        draft: formData,
        errorReason: err?.message || 'fetch_failed',
      });
      setErrors(prev => ({ ...prev, submit: "We couldn't send your message. Please try again, or email sales@porticosystems.com directly." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Layout>
      <SEO 
        title="Contact Us"
        description="Get in touch with Portico Systems for commercial flooring inquiries, quotes, and expert consultation. Our team is ready to help you find the perfect flooring solution."
        canonicalUrl="/contact"
      />
      <LocalBusinessSchema />
      {/* Hero Section */}
      <section className="bg-primary-bg text-primary-foreground py-12 md:py-16">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Let's Start a Conversation
            </h1>
            <p className="text-lg text-primary-foreground/80">
              Our team of flooring experts is ready to help you find the perfect solution for your space.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-1"
            >
              <h2 className="font-heading text-2xl font-bold mb-6">Contact Us</h2>
              
              {/* Headquarters */}
              <div className="mb-8">
                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                  Headquarters
                </h3>
                {contactInfo.headquarters.map((hq) => (
                  <div key={hq.name} className="flex items-start gap-3 mb-3">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">{hq.name}</p>
                      <p className="text-sm text-muted-foreground">{hq.address}</p>
                      <p className="text-sm text-muted-foreground">{hq.country}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Contact Details */}
              <div className="mb-8 space-y-4">
                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                  General Inquiries
                </h3>
                <a 
                  href={`tel:${contactInfo.generalInquiries.phone}`}
                  className="flex items-center gap-3 hover:text-primary transition-colors"
                >
                  <Phone className="w-5 h-5 text-primary" />
                  <span>TF {contactInfo.generalInquiries.phone}</span>
                </a>
                
                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground pt-4">
                  Customer Service
                </h3>
                <a 
                  href={`tel:${contactInfo.customerService.phone}`}
                  className="flex items-center gap-3 hover:text-primary transition-colors"
                >
                  <Phone className="w-5 h-5 text-primary" />
                  <span>{contactInfo.customerService.phone}</span>
                </a>
                <a 
                  href={`mailto:${contactInfo.customerService.email}`}
                  className="flex items-center gap-3 hover:text-primary transition-colors"
                >
                  <Mail className="w-5 h-5 text-primary" />
                  <span>{contactInfo.customerService.email}</span>
                </a>

                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground pt-4">
                  Technical Installation Services
                </h3>
                <a
                  href="mailto:techservices@porticosystems.com"
                  className="flex items-center gap-3 hover:text-primary transition-colors"
                  data-testid="tech-services-email"
                >
                  <Mail className="w-5 h-5 text-primary" />
                  <span>techservices@porticosystems.com</span>
                </a>
              </div>

              {/* Business Hours */}
              <div className="mb-8">
                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                  Business Hours
                </h3>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary" />
                  <span>{contactInfo.businessHours.days}: {contactInfo.businessHours.hours}</span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-col gap-3">
                <a href={`tel:${contactInfo.customerService.phone}`}>
                  <Button variant="default" className="w-full">
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </Button>
                </a>
                <a href={`mailto:${contactInfo.customerService.email}`}>
                  <Button variant="outline" className="w-full">
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-2"
            >
              <div className="bg-card rounded-xl border border-border p-6 md:p-8">
                <h2 className="font-heading text-2xl font-bold mb-6">Send Us a Message</h2>

                {isSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-success" />
                    </div>
                    <h3 className="font-heading font-semibold text-xl mb-2">
                      Thank you!
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Your message has been received. Our team will get back to you shortly.
                    </p>
                    <Button onClick={() => setIsSuccess(false)}>
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <RestoreDraftBanner
                      show={hasDraft}
                      draftAge={draftAge}
                      onRestore={restoreDraft}
                      onDismiss={dismissDraftBanner}
                      testId="contact-restore-draft"
                    />

                    {/* Customer type tabs */}
                    <CustomerTypeTabs value={customerType} onChange={setCustomerType} />

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="How can we help you?"
                        rows={4}
                        className={errors.message ? 'border-destructive' : ''}
                      />
                      {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
                    </div>

                    {/* Name and Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="fullName">Full Name *</Label>
                        <Input
                          id="fullName"
                          value={formData.fullName}
                          onChange={(e) => handleChange('fullName', e.target.value)}
                          placeholder="John Doe"
                          className={errors.fullName ? 'border-destructive' : ''}
                        />
                        {errors.fullName && <p className="text-xs text-destructive">{errors.fullName}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="john@company.com"
                          className={errors.email ? 'border-destructive' : ''}
                        />
                        {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                      </div>
                    </div>

                    {/* Phone and Company */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="(555) 123-4567"
                          className={errors.phone ? 'border-destructive' : ''}
                        />
                        {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Company Name / Organization *</Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          placeholder="Company Inc."
                          className={errors.company ? 'border-destructive' : ''}
                        />
                        {errors.company && <p className="text-xs text-destructive">{errors.company}</p>}
                      </div>
                    </div>

                    {/* Street Address + City */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="streetAddress">Street Address *</Label>
                        <Input
                          id="streetAddress"
                          value={formData.streetAddress}
                          onChange={(e) => handleChange('streetAddress', e.target.value)}
                          placeholder="123 Main Street"
                          className={errors.streetAddress ? 'border-destructive' : ''}
                        />
                        {errors.streetAddress && <p className="text-xs text-destructive">{errors.streetAddress}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          placeholder="City"
                          className={errors.city ? 'border-destructive' : ''}
                        />
                        {errors.city && <p className="text-xs text-destructive">{errors.city}</p>}
                      </div>
                    </div>

                    {/* ZIP / Postal Code */}
                    <div className="space-y-2 max-w-xs">
                      <Label htmlFor="zip">ZIP / Postal Code</Label>
                      <Input
                        id="zip"
                        value={formData.zip}
                        onChange={(e) => handleChange('zip', e.target.value)}
                        placeholder="12345"
                      />
                    </div>

                    {/* Project Title / Architect + Specifier's Location */}
                    <ProjectContextFields
                      values={formData}
                      errors={errors}
                      onChange={handleChange}
                    />

                    {/* Existing-customer-only fields */}
                    <ExistingCustomerFields
                      visible={customerType === 'existing'}
                      values={formData}
                      errors={errors}
                      onChange={handleChange}
                    />

                    {/* Area and State */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Area (Country) *</Label>
                        <Select value={formData.area} onValueChange={(v) => handleChange('area', v)}>
                          <SelectTrigger className={errors.area ? 'border-destructive' : ''}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="United States of America">United States of America</SelectItem>
                            <SelectItem value="Canada">Canada</SelectItem>
                            <SelectItem value="Mexico">Mexico</SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.area && <p className="text-xs text-destructive">{errors.area}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label>State or Province *</Label>
                        <Select value={formData.state} onValueChange={(v) => handleChange('state', v)}>
                          <SelectTrigger className={errors.state ? 'border-destructive' : ''}>
                            <SelectValue placeholder="Select state/province" />
                          </SelectTrigger>
                          <SelectContent>
                            {statesProvinces.map(state => (
                              <SelectItem key={state} value={state}>{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.state && <p className="text-xs text-destructive">{errors.state}</p>}
                      </div>
                    </div>

                    {/* Role and Help Type */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Your Role *</Label>
                        <Select value={formData.role} onValueChange={(v) => handleChange('role', v)}>
                          <SelectTrigger className={errors.role ? 'border-destructive' : ''}>
                            <SelectValue placeholder="Select your role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map(role => (
                              <SelectItem key={role} value={role}>{role}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.role && <p className="text-xs text-destructive">{errors.role}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label>How can we help? *</Label>
                        <Select value={formData.helpType} onValueChange={(v) => handleChange('helpType', v)}>
                          <SelectTrigger className={errors.helpType ? 'border-destructive' : ''}>
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            {helpOptions.map(option => (
                              <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.helpType && <p className="text-xs text-destructive">{errors.helpType}</p>}
                      </div>
                    </div>

                    {/* Checkboxes */}
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="subscribeEmail"
                          checked={formData.subscribeEmail}
                          onCheckedChange={(checked) => handleChange('subscribeEmail', checked)}
                        />
                        <Label htmlFor="subscribeEmail" className="text-sm font-normal cursor-pointer">
                          I'd like to receive information about products, promotions, and news through email.
                        </Label>
                      </div>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="agreePrivacy"
                          checked={formData.agreePrivacy}
                          onCheckedChange={(checked) => handleChange('agreePrivacy', checked)}
                          className={errors.agreePrivacy ? 'border-destructive' : ''}
                        />
                        <Label htmlFor="agreePrivacy" className="text-sm font-normal cursor-pointer">
                          I agree that the above information I have provided can be used in accordance with the{' '}
                          <a href="/privacy" className="text-primary hover:underline">Portico Systems Privacy Policy</a>. *
                        </Label>
                      </div>
                      {errors.agreePrivacy && <p className="text-xs text-destructive">{errors.agreePrivacy}</p>}
                    </div>

                    {errors.submit && (
                      <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg mb-3" data-testid="contact-submit-error">{errors.submit}</p>
                    )}
                    <Button type="submit" size="lg" className="w-full md:w-auto" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Alternative Contact Options */}
      <section className="section-padding-sm bg-muted">
        <div className="container-portico">
          <h2 className="font-heading text-2xl font-bold text-center mb-8">
            Prefer a Different Approach?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div className="bg-card rounded-xl border border-border p-6 text-center">
              <Calendar className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">Schedule a Consultation</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Talk directly with a flooring expert and get guidance, recommendations, and next steps.
              </p>
              <Button variant="outline" onClick={() => setIsSchedulerOpen(true)} data-testid="view-availability-btn">View Availability</Button>
              <p className="text-xs text-muted-foreground mt-2">Free 15-minute consultation. No obligation.</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-6 text-center">
              <Send className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">Get a Fast Quote</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Know what you need? Send us your project details and we'll provide a quick, accurate estimate.
              </p>
              <Button variant="outline" onClick={() => setIsFastQuoteOpen(true)} data-testid="get-my-quote-btn">Get My Quote</Button>
              <p className="text-xs text-muted-foreground mt-2">Response within 1 business day. No obligation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Teams */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <h2 className="font-heading text-2xl font-bold text-center mb-12">
            Meet Our Teams
          </h2>

          {/* Project Managers */}
          <div className="mb-12">
            <h3 className="font-heading font-semibold text-lg mb-6 text-center">Project Managers</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-3xl mx-auto justify-center">
              {teamMembers.projectManagers.map((member) => (
                <TeamCard key={member.name} member={member} />
              ))}
            </div>
          </div>

          {/* Sales Team */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-6 text-center">Sales Team</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {teamMembers.sales.map((member) => (
                <TeamCard key={member.name} member={member} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <ConsultationSchedulerModal
        isOpen={isSchedulerOpen}
        onClose={() => setIsSchedulerOpen(false)}
      />

      <FastQuoteModal
        isOpen={isFastQuoteOpen}
        onClose={() => setIsFastQuoteOpen(false)}
      />

      <SeoContentBlock {...CONTACT_SEO} />
    </Layout>
  );
};

export default ContactUsPage;
